// Native-style pull-to-refresh for the whole app (mounted in Layout).
// Touch-only: drag down while the page is scrolled to the very top.
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { runPullRefresh } from "@/lib/pullRefresh";

const THRESHOLD = 70;   // px of (damped) pull needed to trigger
const MAX_PULL = 110;
// Pages where the gesture fights with scrollable overlays (recipe detail
// sheet) — pull-to-refresh is fully disabled there.
const DISABLED_ROUTES = ["/ricette", "/recipes", "/rezepte", "/recettes"];

export default function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const pullRef = useRef(0);
  const busyRef = useRef(false);
  const { pathname } = useLocation();
  const disabled = DISABLED_ROUTES.some((r) => pathname.includes(r));

  useEffect(() => {
    if (disabled) return;
    if (!("ontouchstart" in window) && !(navigator.maxTouchPoints > 0)) return;

    const scrollTop = () => (document.scrollingElement || document.documentElement).scrollTop;

    const setP = (v) => { pullRef.current = v; setPull(v); };

    // A drag inside an open dialog/sheet must NEVER trigger the page pull —
    // the body behind the overlay is scroll-locked at 0, so without this the
    // gesture would refresh and close the overlay mid-scroll.
    const inOverlay = (el) =>
      !!(el && el.closest && el.closest('[role="dialog"], [data-radix-popper-content-wrapper], [data-sonner-toaster]'));

    const onStart = (e) => {
      if (busyRef.current) return;
      if (document.body.style.overflow === "hidden" || inOverlay(e.target)) {
        startY.current = null;
        return;
      }
      startY.current = scrollTop() <= 0 ? e.touches[0].clientY : null;
    };

    const onMove = (e) => {
      if (startY.current == null || busyRef.current) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta <= 0 || scrollTop() > 0) {
        if (pullRef.current) setP(0);
        return;
      }
      setP(Math.min(delta * 0.45, MAX_PULL));
    };

    const onEnd = async () => {
      startY.current = null;
      if (busyRef.current) return;
      if (pullRef.current >= THRESHOLD) {
        busyRef.current = true;
        setRefreshing(true);
        setP(THRESHOLD);
        try { navigator.vibrate?.(10); } catch {}
        const t0 = Date.now();
        try { await runPullRefresh(); } catch {}
        // keep the spinner visible for at least 500 ms so it doesn't blink
        const wait = Math.max(0, 500 - (Date.now() - t0));
        setTimeout(() => {
          busyRef.current = false;
          setRefreshing(false);
          setP(0);
        }, wait);
      } else {
        setP(0);
      }
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [disabled]);

  const visible = pull > 6 || refreshing;
  const progress = Math.min(pull / THRESHOLD, 1);

  return (
    <div
      data-testid="pull-refresh-indicator"
      aria-hidden={!visible}
      className="fixed left-1/2 z-[70] pointer-events-none"
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + 76px)",
        transform: `translateX(-50%) translateY(${visible ? pull * 0.5 : -20}px)`,
        opacity: visible ? Math.max(progress, 0.35) : 0,
        transition: refreshing || pull === 0 ? "transform 0.25s ease, opacity 0.25s ease" : "none",
      }}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg border border-slate-100 dark:bg-[#22252a] dark:border-slate-700">
        <RefreshCw
          className={`h-5 w-5 text-[#E8951C] ${refreshing ? "animate-spin" : ""}`}
          style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }}
          strokeWidth={2.4}
        />
      </span>
    </div>
  );
}
